/* eslint-disable ember/no-computed-properties-in-native-classes, getter-return */
import Service, { service } from '@ember/service';
import { computed } from '@ember/object';
import { filterBy } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import SchoolType from 'linced-app/enums/school-type';

export default class AttendanceCodesService extends Service {
  @service
  session;

  @computed('session.school.{jurisdiction,schoolType}')
  get allCodes() {
    let codes;
    switch (this.session.school.jurisdiction) {
      case Jurisdiction.AU_NSW:
        return [
          {
            code: '✓',
            inClass: true,
            label: 'Present',
            reason: 'Present',
            truancy: 'P',
            halfDay: 'P',
            quick: true,
            old: false,
            primary: true,
            future: false,
          },
          {
            code: '?',
            inClass: false,
            label: 'Pending Explanation',
            reason: 'Pending Explanation',
            truancy: 'U',
            halfDay: 'A',
            quick: true,
            old: false,
            primary: true,
            future: false,
            tally: 'Unexplained',
          },
          {
            code: 'A',
            inClass: false,
            label: 'Unjustified Explanation Received',
            reason: "Student's absence is unexplained or unjustified",
            truancy: 'U',
            halfDay: 'A',
            quick: true,
            old: false,
            primary: true,
            future: true,
            tally: 'Unjustified',
          },
          {
            code: 'B',
            inClass: false,
            label: 'School Business',
            reason:
              'The student is absent from the school on official school business',
            truancy: 'P',
            halfDay: 'P',
            quick: true,
            old: false,
            primary: true,
            future: true,
          },
          {
            code: 'E',
            inClass: false,
            label: 'Suspended',
            reason: 'Student was suspended from school',
            truancy: 'J',
            halfDay: 'A',
            quick: true,
            old: false,
            primary: true,
            future: true,
          },
          {
            code: 'F',
            inClass: false,
            label: 'Flexible Timetable',
            reason:
              'The student is participating in a flexible timetable and not present because they are not required to be at school',
            truancy: 'P',
            halfDay: 'P',
            quick: true,
            old: false,
            primary: true,
            future: true,
          },
          {
            code: 'H',
            inClass: false,
            label: 'Shared Enrolment',
            reason: 'Attending Alternative Education',
            truancy: 'H',
            halfDay: 'P',
            quick: true,
            old: false,
            primary: true,
            future: true,
          },
          {
            code: 'L',
            inClass: false,
            label: 'Leave (explanation accepted)',
            reason:
              'An explanation of the absence is provided which has been accepted by the principal',
            truancy: 'J',
            halfDay: 'A',
            quick: true,
            old: false,
            primary: true,
            future: true,
          },
          {
            code: 'M',
            inClass: false,
            label: 'Exempted',
            reason:
              'Student was exempted from attending school and a Certificate of Exemption has been issued',
            truancy: 'M',
            halfDay: 'P',
            quick: true,
            old: false,
            primary: true,
            future: true,
          },
          {
            code: 'S',
            inClass: false,
            label: 'Sick',
            reason:
              'Student absent due to short-term illness / Medical reasons',
            truancy: 'J',
            halfDay: 'A',
            quick: true,
            old: false,
            primary: true,
            future: true,
          },
          {
            code: 'U',
            inClass: false,
            label: 'Unexplained after 7 days',
            reason: 'Absence unexplained after 7 days',
            truancy: 'U',
            halfDay: 'A',
            quick: false,
            old: false,
            primary: true,
            future: true,
            tally: 'Unexplained',
          },
        ];
    }

    // Default to NZ MoE codes.
    codes = [
      {
        code: '?',
        inClass: false,
        label: 'Unknown reason',
        reason: 'Unknown reason',
        truancy: 'U',
        halfDay: 'A',
        quick: true,
        old: false,
        primary: true,
        future: false,
        tally: 'Unexplained',
      },
      {
        code: 'P',
        inClass: true,
        label: 'Present',
        reason: 'Present',
        truancy: 'P',
        halfDay: 'P',
        quick: true,
        old: false,
        primary: true,
        future: false,
      },
      {
        code: 'L',
        inClass: true,
        label: 'Late to class',
        reason: 'Late to class',
        truancy: 'P',
        halfDay: 'P',
        quick: true,
        old: false,
        primary: true,
        future: false,
        late: true,
      },
      {
        code: 'M',
        inClass: false,
        label: 'Illness / Medical absence',
        reason: 'Illness / Medical absence',
        truancy: 'J',
        halfDay: 'A',
        quick: true,
        old: false,
        primary: true,
        future: true,
      },
      {
        code: 'D',
        inClass: false,
        label: 'Approved external appointment',
        reason: 'Approved external appointment',
        truancy: 'P',
        halfDay: 'P',
        quick: true,
        old: false,
        primary: true,
        future: true,
      },
      {
        code: 'S',
        inClass: false,
        label: 'Sickbay',
        reason: 'Sickbay',
        truancy: 'P',
        halfDay: 'P',
        quick: true,
        old: true,
        primary: true,
        future: true,
      },
      {
        code: 'G',
        inClass: false,
        label: 'Holiday during term time',
        reason: 'Holiday during term time',
        truancy: 'U',
        halfDay: 'A',
        quick: true,
        old: false,
        primary: true,
        future: true,
      },
      {
        code: 'Q',
        inClass: false,
        label: 'Board approved offsite learning',
        reason: 'Board approved offsite learning',
        truancy: 'P',
        halfDay: 'P',
        quick: true,
        old: false,
        primary: true,
        future: true,
      },
      {
        code: 'J',
        inClass: false,
        label: 'Explained & approved',
        reason: 'Explained & approved',
        truancy: 'J',
        halfDay: 'A',
        quick: true,
        old: false,
        primary: true,
        future: true,
      },
      {
        code: 'E',
        inClass: false,
        label: 'Explained but not approved',
        reason: 'Explained but not approved',
        truancy: 'U',
        halfDay: 'A',
        quick: true,
        old: false,
        primary: true,
        future: true,
      },
      {
        code: 'T',
        inClass: false,
        label: 'Truant',
        reason: 'Truant',
        truancy: 'U',
        halfDay: 'A',
        quick: true,
        old: false,
        primary: true,
        future: true,
        tally: 'Unexplained',
      },
      {
        code: 'N',
        inClass: false,
        label: 'Present but out of class',
        reason: 'Present but out of class',
        truancy: 'P',
        halfDay: 'P',
        quick: true,
        old: false,
        primary: true,
        future: true,
      },
      {
        code: 'I',
        inClass: false,
        label: 'Internal appointment',
        reason: 'Internal school appointment or activity',
        truancy: 'P',
        halfDay: 'P',
        quick: false,
        old: true,
        primary: true,
        future: true,
      },
      {
        code: 'H',
        inClass: false,
        label: 'Health School',
        reason:
          'Attending a Health school / Regional Health School / Residential School',
        truancy: 'J',
        halfDay: 'P',
        quick: false,
        old: true,
        primary: true,
        future: true,
      },
      {
        code: 'U',
        inClass: false,
        label: 'Stood down or Suspended',
        reason: 'Stood down or Suspended',
        truancy: 'J',
        halfDay: 'A',
        quick: false,
        old: false,
        primary: true,
        future: true,
      },
      {
        code: 'O',
        inClass: false,
        label: 'Overseas Posting',
        reason: 'Overseas Posting',
        truancy: 'O',
        halfDay: 'A',
        quick: false,
        old: true,
        primary: true,
        future: true,
      },
      {
        code: 'R',
        inClass: false,
        label: 'Not in class',
        reason: 'Removed (temporarily) from regular class',
        truancy: 'P',
        halfDay: 'P',
        quick: false,
        old: true,
        primary: true,
        future: true,
      },
      {
        code: 'F',
        inClass: false,
        label: 'Offsite',
        reason: 'Attending an offsite course/class',
        truancy: 'P',
        halfDay: 'P',
        quick: true,
        old: true,
        primary: true,
        future: true,
      },
      {
        code: 'A',
        inClass: false,
        label: 'Alternative provision',
        reason: 'Alternative provision',
        truancy: 'P',
        halfDay: 'P',
        quick: false,
        old: false,
        primary: true,
        future: true,
      },
      {
        code: 'W',
        inClass: false,
        label: 'Work experience',
        reason: 'Work experience',
        truancy: 'P',
        halfDay: 'P',
        quick: false,
        old: true,
        primary: false,
        future: true,
      },
      {
        code: 'K',
        inClass: false,
        label: 'Teen Parent Unit',
        reason: 'Attending a Teen Parent Unit',
        truancy: 'J',
        halfDay: 'P',
        quick: false,
        old: true,
        primary: false,
        future: true,
      },
      {
        code: 'Y',
        inClass: false,
        label: 'Act. Centre',
        reason: 'Attending an Activity centre',
        truancy: 'J',
        halfDay: 'P',
        quick: false,
        old: true,
        primary: false,
        future: true,
      },
      {
        code: 'C',
        inClass: false,
        label: 'Court',
        reason: 'Student is attending Justice Court proceedings',
        truancy: 'J',
        halfDay: 'P',
        quick: false,
        old: true,
        primary: false,
        future: true,
      },
      {
        code: 'Z',
        inClass: false,
        label: 'Secondary Tertiary Programme',
        reason: 'Secondary Tertiary Programme (including Trades Academies)',
        truancy: 'P',
        halfDay: 'P',
        quick: false,
        old: true,
        primary: false,
        future: true,
      },
    ];
    if (
      parseInt(this.session.school.schoolType, 10) >
      SchoolType.INTERMEDIATE_SCHOOL
    ) {
      codes = [
        ...codes,
        {
          code: 'X',
          inClass: false,
          label: 'Exam leave',
          reason: 'Exam leave',
          truancy: 'J',
          halfDay: 'A',
          quick: false,
          old: false,
          primary: false,
          future: true,
        },
        {
          code: 'V',
          inClass: false,
          label: 'Unsupervised exam study',
          reason: 'Unsupervised exam study',
          truancy: 'P',
          halfDay: 'P',
          quick: false,
          old: false,
          primary: false,
          future: true,
        },
      ];
    }
    return codes;
  }

  @filterBy('allCodes', 'quick')
  quickCodes;

  @filterBy('allCodes', 'future')
  futureCodes;

  @filterBy('allCodes', 'truancy', 'P')
  truancyPCodes;

  @filterBy('allCodes', 'truancy', 'J')
  truancyJCodes;

  @filterBy('allCodes', 'truancy', 'U')
  truancyUCodes;

  @filterBy('allCodes', 'truancy', 'O')
  truancyOCodes;

  @filterBy('allCodes', 'halfDay', 'P')
  presentPCodes;

  @filterBy('allCodes', 'halfDay', 'A')
  presentACodes;

  @computed('session.school.jurisdiction', 'allCodes.@each.code')
  get present() {
    switch (this.session.school.jurisdiction) {
      case Jurisdiction.AU_NSW:
        return findBy(this.allCodes, 'code', '✓');
    }

    // Default to NZ MoE code.
    return findBy(this.allCodes, 'code', 'P');
  }

  @computed('session.school.jurisdiction', 'allCodes.@each.code')
  get medicalAbsence() {
    switch (this.session.school.jurisdiction) {
      case Jurisdiction.AU_NSW:
        return findBy(this.allCodes, 'code', 'S');
    }

    // Default to NZ MoE code.
    return findBy(this.allCodes, 'code', 'M');
  }

  @computed('session.school.jurisdiction', 'allCodes.@each.code')
  get sickbay() {
    switch (this.session.school.jurisdiction) {
      case Jurisdiction.AU_NSW:
        // NSW has no "in sickbay" code.
        return;
    }

    // Default to NZ MoE code.
    return findBy(this.allCodes, 'code', 'N');
  }

  @computed('session.school.jurisdiction')
  get bulkOffsiteLabel() {
    switch (this.session.school.jurisdiction) {
      case Jurisdiction.AU_NSW:
        return 'School Business';
    }

    return 'Offsite';
  }

  @computed('session.school.jurisdiction', 'allCodes.@each.code')
  get bulkOffsite() {
    switch (this.session.school.jurisdiction) {
      case Jurisdiction.AU_NSW:
        return [findBy(this.allCodes, 'code', 'B')];
    }

    // Default to NZ MoE codes.
    return [
      findBy(this.allCodes, 'code', 'Q'),
      findBy(this.allCodes, 'code', 'F'),
      findBy(this.allCodes, 'code', 'J'),
    ];
  }

  @computed('allCodes.@each.code')
  get default() {
    return findBy(this.allCodes, 'code', '?');
  }

  @computed('allCodes.@each.code')
  get unknown() {
    return findBy(this.allCodes, 'code', '?');
  }

  @computed('allCodes.@each.{truancy,code,late}')
  get truancyClasses() {
    return this.allCodes.reduce((map, code) => {
      switch (code.truancy) {
        case 'M':
        case 'H':
        case 'I':
        case 'P':
          map[code.code] = 'attendance-present';
          break;
        case 'U':
          map[code.code] = 'attendance-unjustified';
          break;
        case 'J':
        case 'O':
          map[code.code] = 'attendance-justified';
          break;
        default:
          map[code.code] = '';
      }
      if (code.late) {
        map[code.code] = 'attendance-late';
      }
      return map;
    }, {});
  }

  @computed('allCodes.@each.{code,inClass,label,tally}')
  get tallyMap() {
    return this.allCodes.reduce((map, code) => {
      if (!code.inClass) {
        if (!isEmpty(code.tally)) {
          map[code.code] = code.tally;
        } else {
          map[code.code] = code.label;
        }
      }
      return map;
    }, {});
  }

  @computed('allCodes.@each.{code,truancy}')
  get truancyMap() {
    return this.allCodes.reduce((map, code) => {
      map[code.code] = code.truancy;
      return map;
    }, {});
  }

  getLabelForTruancyCode(code) {
    switch (code) {
      case 'I':
      case 'P':
        return 'Present';
      case 'U':
        return 'Unjustified';
      case 'J':
        return 'Justified';
      case 'O':
        return 'Overseas Posting';
      default:
        return 'Unknown';
    }
  }
}
